/**
 * DataTable Component
 * Modern data table with sorting, pagination, and loading states
 */

import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Box,
  Typography,
  Skeleton,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  '& .MuiTableCell-root': {
    borderColor: theme.palette.divider,
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    padding: '12px 16px',
    whiteSpace: 'nowrap',
  },
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& .MuiTableRow-root': {
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(105, 203, 249, 0.08)',
      '&:hover': {
        backgroundColor: 'rgba(105, 203, 249, 0.12)',
      },
    },
  },
  '& .MuiTableCell-body': {
    padding: '14px 16px',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
}));

const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  '& .MuiTablePagination-select': {
    fontSize: '0.875rem',
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  selectable = false,
  selected = [],
  onSelectChange,
  sortable = true,
  defaultSort,
  defaultSortDirection = 'asc',
  pagination = true,
  page = 0,
  rowsPerPage = 10,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  onRowClick,
  emptyMessage = 'No data available',
  emptyIcon,
  stickyHeader = false,
  maxHeight,
  getRowId = (row) => row.id || row._id,
  className,
  ...props
}) => {
  const [internalSort, setInternalSort] = useState(defaultSort || '');
  const [internalSortDirection, setInternalSortDirection] = useState(defaultSortDirection);
  const [internalPage, setInternalPage] = useState(0);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(rowsPerPage);

  // Use internal or external pagination state
  const currentPage = onPageChange ? page : internalPage;
  const currentRowsPerPage = onRowsPerPageChange ? rowsPerPage : internalRowsPerPage;

  // Sort data if sortable and not server-side
  const sortedData = useMemo(() => {
    if (!sortable || !internalSort || totalCount !== undefined) {
      return data;
    }
    return [...data].sort(getComparator(internalSortDirection, internalSort));
  }, [data, sortable, internalSort, internalSortDirection, totalCount]);

  // Paginate data if not server-side
  const paginatedData = useMemo(() => {
    if (!pagination || totalCount !== undefined) {
      return sortedData;
    }
    const start = currentPage * currentRowsPerPage;
    return sortedData.slice(start, start + currentRowsPerPage);
  }, [sortedData, pagination, currentPage, currentRowsPerPage, totalCount]);

  const handleSort = (columnId) => {
    const isAsc = internalSort === columnId && internalSortDirection === 'asc';
    setInternalSortDirection(isAsc ? 'desc' : 'asc');
    setInternalSort(columnId);
  };

  const handlePageChange = (event, newPage) => {
    if (onPageChange) {
      onPageChange(event, newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(event);
    } else {
      setInternalRowsPerPage(newRowsPerPage);
      setInternalPage(0);
    }
  };

  const handleSelectAll = (event) => {
    if (!onSelectChange) return;
    if (event.target.checked) {
      const newSelected = paginatedData.map((row) => getRowId(row));
      onSelectChange(newSelected);
    } else {
      onSelectChange([]);
    }
  };

  const handleSelectRow = (event, id) => {
    event.stopPropagation();
    if (!onSelectChange) return;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    onSelectChange(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;
  const numSelected = selected.length;
  const rowCount = paginatedData.length;
  const actualTotalCount = totalCount !== undefined ? totalCount : sortedData.length;

  // Loading skeleton
  if (loading) {
    return (
      <StyledTableContainer component={Paper} elevation={0} className={className}>
        <Table stickyHeader={stickyHeader}>
          <StyledTableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Skeleton variant="rectangular" width={18} height={18} />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell key={column.id} style={{ width: column.width, minWidth: column.minWidth }}>
                  <Skeleton variant="text" width={80} />
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <StyledTableBody>
            {[...Array(currentRowsPerPage)].map((_, index) => (
              <TableRow key={index}>
                {selectable && (
                  <TableCell padding="checkbox">
                    <Skeleton variant="rectangular" width={18} height={18} />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <StyledTableContainer component={Paper} elevation={0} className={className}>
        <Table>
          <StyledTableHead>
            <TableRow>
              {selectable && <TableCell padding="checkbox" />}
              {columns.map((column) => (
                <TableCell key={column.id} style={{ width: column.width, minWidth: column.minWidth }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
        </Table>
        <EmptyState>
          {emptyIcon && <Box sx={{ mb: 2, opacity: 0.5 }}>{emptyIcon}</Box>}
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </EmptyState>
      </StyledTableContainer>
    );
  }

  return (
    <StyledTableContainer
      component={Paper}
      elevation={0}
      className={className}
      sx={{ maxHeight: maxHeight }}
      {...props}
    >
      <Table stickyHeader={stickyHeader}>
        <StyledTableHead>
          <TableRow>
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={rowCount > 0 && numSelected === rowCount}
                  onChange={handleSelectAll}
                  size="small"
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                style={{ width: column.width, minWidth: column.minWidth }}
                sortDirection={internalSort === column.id ? internalSortDirection : false}
              >
                {sortable && column.sortable !== false ? (
                  <TableSortLabel
                    active={internalSort === column.id}
                    direction={internalSort === column.id ? internalSortDirection : 'asc'}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                    {internalSort === column.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {internalSortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </StyledTableHead>
        <StyledTableBody>
          {paginatedData.map((row, index) => {
            const rowId = getRowId(row);
            const isItemSelected = isSelected(rowId);

            return (
              <TableRow
                key={rowId || index}
                hover
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                role={selectable ? 'checkbox' : undefined}
                aria-checked={isItemSelected}
                selected={isItemSelected}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isItemSelected}
                      onClick={(event) => handleSelectRow(event, rowId)}
                      size="small"
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align || 'left'}>
                    {column.render ? column.render(row[column.id], row, index) : row[column.id]}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </StyledTableBody>
      </Table>
      {pagination && (
        <StyledTablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={actualTotalCount}
          rowsPerPage={currentRowsPerPage}
          page={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}
    </StyledTableContainer>
  );
};

export default DataTable;
